import { Link } from 'react-router'
import { ClientLabelBadge, ListingFeatured } from '~/components/listing'
import { Badge } from '~/components/ui'
import { STATUSES, TYPES } from '~/constants'
import useMapListingLink from '~/hooks/map/useMapListingLink'
import { cn, fmtAddress, getPropertyTypeClassName } from '~/lib'
import { type Listing } from '~/models'

export default function ListingInfo({
  listing,
}: {
  listing: Listing
  showOriginalBrokers?: boolean
}) {
  const listingLink = useMapListingLink(listing)

  const renderTypeStatus = () => {
    return (
      <Badge
        className={cn(
          'left-3 text-base',
          listing.type === 'sale' && 'bg-[#0284c7] hover:bg-[#0284c7]',
          listing.type === 'lease' && 'bg-[#c026d3] hover:bg-[#c026d3]',
          listing.type === 'sale/lease' && 'bg-[#4338ca] hover:bg-[#4338ca]'
        )}>
        {listing.status === 'active'
          ? TYPES[listing.type]
          : STATUSES[listing.status]}
      </Badge>
    )
  }

  const renderLabels = () => {
    if (!listing.labels || listing.labels.length === 0) return null

    return listing.labels.map((label) => {
      return <ClientLabelBadge key={label.id} label={label} />
    })
  }

  const renderPropertyTypes = () => {
    if (listing.property_types === null || undefined) {
      return (
        <Badge className={cn('text-base', getPropertyTypeClassName('Other'))}>
          <span>Other</span>
        </Badge>
      )
    }
    return (
      <>
        {listing.property_types.map((propertyType, index) => (
          <Badge
            key={index}
            className={cn('text-base', getPropertyTypeClassName(propertyType))}>
            <span>{propertyType}</span>
          </Badge>
        ))}
      </>
    )
  }

  return (
    <div className="">
      <h3 className="flex items-center gap-1 text-xl font-medium text-foreground">
        {listing.featured && <ListingFeatured className="h-5 w-5" />}
        <Link
          to={listingLink}
          target="_blank"
          className="underline-offset-4 hover:underline"
          onClick={(e) => e.stopPropagation()}>
          {listing.name || listing.property.address || ''}
        </Link>
      </h3>
      <div className="max-w-2xl text-lg font-light text-muted-foreground">
        <p>{fmtAddress(listing.property, true)}</p>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {renderTypeStatus()}
        {renderLabels()}
        {renderPropertyTypes()}
      </div>
      {(listing.meta.property_subtypes || []).length > 0 && (
        <p className="mt-3 italic">
          Secondary Uses: {listing.meta.property_subtypes!.join(', ')}
        </p>
      )}
    </div>
  )
}
